import type { PlaceOpeningHours } from '@elara/domain/models/schemas/place-schemas';

import { cn } from '@elara/client-core/lib/utils';
import { formatTimeOfDay } from '@elara/domain/opening-hours';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export function OpeningHoursTable({
  openingHours,
  highlightToday = false,
  orderByToday = false,
  isOpenNow = false,
}: {
  openingHours: PlaceOpeningHours;
  highlightToday?: boolean;
  orderByToday?: boolean;
  isOpenNow?: boolean;
}) {
  const todayIndex = new Date().getDay();
  const dayIndices = orderByToday ? Array.from({ length: 7 }, (_, i) => (todayIndex + i) % 7) : [0, 1, 2, 3, 4, 5, 6];

  // 24/7: single period with open day=0 at 0:00 and no close (Google's representation)
  const isOpen247 = openingHours?.periods?.some(
    (p) => p.open?.day === 0 && p.open?.hour === 0 && p.open?.minute === 0 && !p.close,
  );

  return (
    <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1">
      {dayIndices.map((dayIndex) => {
        const dayName = DAY_NAMES[dayIndex];
        const dayPeriods = openingHours?.periods?.filter((p) => p.open?.day === dayIndex) ?? [];

        const is24HourDay = isOpen247;
        const isToday = dayIndex === todayIndex;

        let value: string;
        if (is24HourDay) {
          value = 'Open 24 hours';
        } else {
          const ranges = dayPeriods
            .filter(
              (p) => p.open?.hour != null && p.open?.minute != null && p.close?.hour != null && p.close?.minute != null,
            )
            .map((p) => {
              const openHour = p.open!.hour as number;
              const openMinute = p.open!.minute as number;
              const closeHour = p.close!.hour as number;
              const closeMinute = p.close!.minute as number;
              return `${formatTimeOfDay(openHour, openMinute)} – ${formatTimeOfDay(closeHour, closeMinute)}`;
            });

          value = ranges.length > 0 ? ranges.join(', ') : 'Closed';
        }

        return (
          <div
            key={dayName}
            className={cn(
              'flex items-center justify-between gap-1',
              highlightToday && (dayIndex === todayIndex ? 'font-medium text-foreground' : 'text-muted-foreground'),
            )}
          >
            <div>
              {dayName}
              {isToday && ' - '}
              {isToday &&
                (isOpenNow ? (
                  <span className="text-success">Open now</span>
                ) : (
                  <span className="text-destructive">Closed</span>
                ))}
            </div>
            <div>{value}</div>
          </div>
        );
      })}
    </div>
  );
}
